// React
import { useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';

// Hooks
import { usePreviewData } from '@/hooks/usePreviewData';

// Services
import { getHeader } from '@/services/header/headerService';
import { getFooter } from '@/services/footer/footerService';
import { getContact } from '@/services/contact/contactService';

// Components
import LayoutHeaderFooter from '@/components/layout/LayoutHeaderFooter/LayoutHeaderFooter';
import { PageBlocks } from '@/components/blocks/Blocks';
import TemplateContact from '@/components/templates/TemplateContact/TemplateContact';

// Styles
import { Helmet } from 'react-helmet';
import { RoutesEnum } from '@/enum/RoutesEnum';
import styles from './Contact.module.css';

export async function contactLoader() {
  const [header, footer, page] = await Promise.all([getHeader(), getFooter(), getContact()]);

  return {
    header,
    footer,
    page,
  };
}

function Contact() {
  const loaderData = useLoaderData() as Awaited<ReturnType<typeof contactLoader>>;
  const refetch = useCallback(() => contactLoader(), []);
  const data = usePreviewData(loaderData, refetch);
  const baseUrl = import.meta.env.VITE_DOMAIN;

  if (!data) return null;

  const { header, footer, page } = data;

  if (!page) return null;

  return (
    <main>
      <Helmet>
        <title>{page.seo.metaTitle}</title>
        <meta name="description" content={page.seo.metaDescription} />
        <link rel="canonical" href={`${baseUrl}/${RoutesEnum.CONTACT}`} />
      </Helmet>
      <LayoutHeaderFooter header={header} footer={footer} breadcrumb>
        <div className={styles.body}>
          <TemplateContact
            title={page.title}
            paragraph={page.paragraph}
            expertises={page.expertises}
            origins={page.origins}
            phone={footer.phone}
            mail={footer.mail}
            addresses={footer.addresses}
            socialMedia={footer.socialMedia}
            picture={page.picture}
          >
            {page.blocks ? <PageBlocks blocks={page.blocks} /> : null}
          </TemplateContact>
        </div>
      </LayoutHeaderFooter>
    </main>
  );
}

export default Contact;
